import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react"

export function Toaster() {
  const { toasts } = useToast()

  const detectSuccessVariant = (variant?: string | null, title?: React.ReactNode, description?: React.ReactNode): "default" | "success" | "warning" | "destructive" => {
    // Dacă variant este deja setat, folosim-l
    if (variant && (variant === 'success' || variant === 'warning' || variant === 'destructive')) {
      return variant as "success" | "warning" | "destructive"
    }

    // Convertim title și description la string pentru verificare
    const titleText = typeof title === 'string' ? title : String(title || '')
    const descriptionText = typeof description === 'string' ? description : String(description || '')
    const combinedText = `${titleText} ${descriptionText}`.toLowerCase()

    // Cuvinte cheie pentru mesaje de succes
    const successKeywords = [
      'succes', 'success', 'reușit', 'reusit', 'aplicat', 'aplicată', 'anulat', 'anulată',
      'șters', 'sters', 'creat', 'creată', 'actualizat', 'actualizată', 'salvat', 'salvată',
      'optimizat', 'optimizată', 'finalizat', 'finalizată', 'completat', 'completată'
    ]

    // Verificăm dacă textul conține cuvinte de succes
    const isSuccess = successKeywords.some(keyword => combinedText.includes(keyword))
    
    return isSuccess ? 'success' : 'default'
  }

  const getIcon = (variant?: "default" | "success" | "warning" | "destructive" | null) => {
    const iconStyle = { color: 'white', fontSize: '1.25rem' }
    switch (variant) {
      case "success":
        return <CheckCircle2 className="flex-shrink-0 h-5 w-5" style={iconStyle} />
      case "warning":
        return <AlertTriangle className="flex-shrink-0 h-5 w-5" style={iconStyle} />
      case "destructive":
        return <XCircle className="flex-shrink-0 h-5 w-5" style={iconStyle} />
      default:
        return null
    }
  }

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        const detectedVariant = detectSuccessVariant(variant, title, description)
        const icon = getIcon(detectedVariant)
        
        return (
          <Toast key={id} variant={detectedVariant} {...props}>
            <div className="flex items-start gap-3 flex-1">
              {icon && <div className="mt-0.5">{icon}</div>}
              <div className="grid gap-1 flex-1">
                {title && (
                  <ToastTitle className={detectedVariant === 'success' || detectedVariant === 'warning' || detectedVariant === 'destructive' ? 'text-white' : ''}>
                    {title}
                  </ToastTitle>
                )}
                {description && (
                  <ToastDescription className={detectedVariant === 'success' || detectedVariant === 'warning' || detectedVariant === 'destructive' ? 'text-white' : ''}>
                    {description}
                  </ToastDescription>
                )}
              </div>
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
